import React, {useEffect, useRef} from "react"
import TitleBar from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import EmailTemplates from "./EmailTemplates/EmailTemplates"
import EmailTemplateVersions from "./EmailTemplates/EmailTemplateVersions"
import ManagedSolutionsAdmin from "./ManagedSolutionsAdmin/ManagedSolutionsAdmin"
import {useParams, useSearchParams} from "react-router-dom"
import {useQueryState} from "use-location-state"
import WireServiceMessage from "./WireServiceMessage/ServiceMessage"
import BillPayTermsList from "../BillPayTerms/BillPayTermsList"
import BillPayOrgSettingsList from "../BillPayOrgSettings/BillPayOrgSettingsList"
import {useIsUnitReadonlyUser} from "../../services/auth"
import {hasPermission} from "../../services/permission"

type AdminTabs =
	| "Email Templates"
	| "Managed Solutions"
	| "Wire Service Message"
	| "Bill Pay Terms"
	| "Bill Pay Org Settings"

const ADMIN_TABS: AdminTabs[] = [
	"Email Templates",
	"Managed Solutions",
	"Wire Service Message",
	"Bill Pay Terms",
	"Bill Pay Org Settings",
]

function TabContent({
	currentTab,
	refresh,
	templateId,
	templateName,
}: {
	currentTab: AdminTabs
	refresh: () => void
	templateId?: string
	templateName?: string
}) {
	if (templateId) {
		return <EmailTemplateVersions templateId={templateId} templateName={templateName} refresh={refresh} />
	}

	switch (currentTab) {
		case "Managed Solutions":
			return <ManagedSolutionsAdmin />
		case "Wire Service Message":
			return <WireServiceMessage />
		case "Bill Pay Terms":
			return <BillPayTermsList />
		case "Bill Pay Org Settings":
			return <BillPayOrgSettingsList />
		case "Email Templates":

		default:
			return <EmailTemplates refresh={refresh} />
	}
}

function AdminPageInternal({
	refresh,
	templateId,
	templateName,
}: {
	refresh: () => void
	templateId?: string
	templateName?: string
}) {
	const isUnitReadonly = useIsUnitReadonlyUser()
	const canViewManagedSolutions = hasPermission("app.config", "get")
	const availableTabs = isUnitReadonly
		? ADMIN_TABS.filter((tab) => tab === "Email Templates" || (tab === "Managed Solutions" && canViewManagedSolutions))
		: ADMIN_TABS
	const [currentTab, setCurrentTab] = useQueryState<AdminTabs>("tab", "Email Templates")
	const [searchParams] = useSearchParams()
	const hasSynced = useRef(false)

	useEffect(() => {
		if (hasSynced.current) return
		hasSynced.current = true
		const tabParam = searchParams.get("tab")
		if (tabParam && availableTabs.includes(tabParam as AdminTabs) && tabParam !== currentTab) {
			setCurrentTab(tabParam as AdminTabs)
		}
	}, [searchParams, currentTab, setCurrentTab])

	const title = templateId ? `Email Template Versions` : "Admin Dashboard"

	const breadcrumbs = templateId ? [{text: "Email Templates", path: "/admin"}] : undefined

	return (
		<>
			<TitleBar title={title} className={"title-bar-no-border"} breadcrumbs={breadcrumbs} />

			{!templateId && (
				<nav className="admin-settings tabs-container">
					<div className="tabs">
						<ul>
							{availableTabs.map((tab) => (
								<li key={tab} className={currentTab === tab ? "is-active" : ""}>
									<a onClick={() => setCurrentTab(tab)} style={{cursor: "pointer"}}>
										{tab}
									</a>
								</li>
							))}
						</ul>
					</div>
				</nav>
			)}

			<MainSection>
				<TabContent currentTab={currentTab} refresh={refresh} templateId={templateId} templateName={templateName} />
			</MainSection>
		</>
	)
}

export default function AdminPage() {
	// Create refreshToken once and use it only for the refresh function
	const [, refresh] = useRefreshToken()
	const params = useParams<{templateId?: string; templateName?: string}>()
	const templateId = params.templateId
	const templateName = params.templateName ? decodeURIComponent(params.templateName) : undefined

	return <AdminPageInternal refresh={refresh} templateId={templateId} templateName={templateName} />
}
